import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DataTablesComp from "components/dataTable";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Button from "components/Button";
import Input from "components/inputField";
import "./OrderInformationFileTable.scss";

import { applyCouponCode } from "redux/asyncApi/uploadApi";
import { clearUploadErrors } from "redux/Slices/uploadSlice";
import {
  currencyFormatter,
  dateFormat,
  noRecordFound,
  removeUnderScore,
} from "util/helpers";
import { validationMsg } from "util/validationErrors";
import DataTablev2 from "components/dataTablev2";
import OptionalServicesTable from "./OptionalServicesTable";

const OrderInformationFileTable = ({
  orderData,
  balance_payable,
  total,
  total_discount,
  coupon_name,
  optionalServicesData
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState({
    apply_coupon: coupon_name,
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  //redux state
  const {
    loading,
    userAuth: { token },
  } = useSelector((state) => state.app) || {};
  const {
    uploadedFiles,
    error: { message },
    selectedUser,
  } = useSelector((state) => state.upload);

  //redux action dispatcher
  const dispatch = useDispatch();

  //router navigate function
  const navigate = useNavigate();

  // ref
  const couponRef = useRef();

  //methods

  useEffect(() => {
    if (coupon_name) {
      setData({ ...data, apply_coupon: coupon_name });
    } else {
      setData({ ...data, apply_coupon: "" });
    }
  }, [coupon_name]);

  const onCouponChangeHandler = (e) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  useEffect(() => {
    couponRef?.current?.focus();
    if (message) {
      setFormErrors({ ...formErrors, apply_coupon: message });
      couponRef?.current?.focus();
    }
    return () => {
      dispatch(clearUploadErrors());
    };
  }, [message]);

  useEffect(() => {
    if (isSubmit) {
      const values = {
        order_temp_id: uploadedFiles?.order_temp_id,
        coupon_code: data.apply_coupon,
        user_id: selectedUser?.value,
      };
      dispatch(
        applyCouponCode({
          token,
          values,
        })
      );
    }
    setIsSubmit(false);
  }, [isSubmit, dispatch, data.apply_coupon, token]);

  const validateForm = (values) => {
    const errors = {};
    if (values.apply_coupon === "") {
      errors.apply_coupon = validationMsg.couponReq;
      couponRef?.current?.focus();
      couponRef?.current?.click();
    }
    if (Object.keys(errors).length === 0) {
      setIsSubmit(true);
    }
    return errors;
  };

  const applyCouponHandler = () => {
    setFormErrors(validateForm(data));
  };

  const cellRendrer = useCallback(
    (cell, row, column, index) => {
      if (column === "original_file_name") {
        return (
          <div id={row.id} className="file_name_head">
            {row.original_file_name}
          </div>
        );
      }

      if (column === "file_type") {
        return (
          <div id={row.id}>
            <span className="role uppercase">
              {removeUnderScore(row.file_type ?? "")}
            </span>
          </div>
        );
      }
      if (column === "file_deliver_date") {
        return (
          <div>
            <span className="role">
              {moment(row.file_deliver_date).format(dateFormat)}
            </span>
          </div>
        );
      }

      if (column === "file_page_count") {
        return (
          <div id={row.id}>
            <span className="role">{row.file_page_count}</span>
          </div>
        );
      }
      if (column === "sla_type") {
        return (
          <div id={row.id}>
            <span className="role uppercase">{row.sla_type}</span>
          </div>
        );
      }
      if (column === "file_estimate_cost") {
        return (
          <div>
            <span className="role">
              {currencyFormatter(row.file_estimate_cost)}
            </span>
          </div>
        );
      }

      return row[column];
    },
    [navigate]
  );

  const tableHead = [
    {
      key: "original_file_name",
      title: "FILE NAME",
      sorting: true,
      searching: true,
      width: "30%",
    },
    {
      key: "file_type",
      title: "Type",
      sorting: true,
      searching: false,
      width: "15%",
    },
    {
      key: "file_deliver_date",
      title: "Date of Delivery",
      sorting: true,
      searching: false,
      width: "15%",
    },

    {
      key: "file_page_count",
      title: "Number Of Pages",
      sorting: true,
      searching: false,
      width: "15%",
    },
    {
      key: "sla_type",
      title: "Delivery Type",
      sorting: true,
      searching: false,
      width: "15%",
    },
    {
      key: "file_estimate_cost",
      title: "Total Cost",
      sorting: true,
      searching: false,
      width: "10%",
    },
  ];

  const columns = [
    {
      id: "original_file_name",
      accessoryKey: "original_file_name",
      header: "File Name",
      cell: ({ row }) => {
        return (
          <div id={row?.original?.id} className="file_name_head">
            {row?.original?.original_file_name}
          </div>
        );
      },
    },
    {
      id: "file_type",
      accessoryKey: "file_type",
      header: "File Type",
      cell: ({ row }) => {
        return (
          <div id={row?.original?.id}>
            <span className="role uppercase">
              {removeUnderScore(row?.original?.file_type ?? "")}
            </span>
          </div>
        );
      },
    },
    {
      id: "file_deliver_date",
      accessoryKey: "file_deliver_date",
      header: "Date of Delivery",
      cell: ({ row }) => {
        return (
          <div id={row?.original?.id}>
            <span className="role">
              {moment(row?.original?.file_deliver_date).format(dateFormat)}
            </span>
          </div>
        );
      },
    },
    {
      id: "file_page_count",
      accessoryKey: "file_page_count",
      header: "Number of Pages",
      cell: ({ row }) => {
        return (
          <div id={row?.original?.id}>
            <span className="role">{row?.original?.file_page_count}</span>
          </div>
        );
      },
    },
    {
      id: "sla_type",
      accessoryKey: "sla_type",
      header: "Delivery Type",
      cell: ({ row }) => {
        return (
          <div id={row?.original?.id}>
            <span className="role uppercase">{row?.original?.sla_type}</span>
          </div>
        );
      },
    },
    {
      id: "file_estimate_cost",
      accessoryKey: "file_estimate_cost",
      header: "Total Cost",
      cell: ({ row }) => {
        return (
          <div>
            <span className="role">
              {currencyFormatter(row?.original.file_estimate_cost)}
            </span>
          </div>
        );
      },
    },
  ]

  return (
    <>
      <div className="dt-responsive">
        {/* {!loading && orderData.length > 0 && ( */}
        <>
          {/* <DataTablesComp
            tableHead={tableHead}
            data={orderData}
            renderCell={cellRendrer}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            setPageSize={setPageSize}
            pageSize={pageSize}
            type="order"
            search={false}
            pageSizing={true}
            paginationBar={true}
            // pageSize={true}
            tableDescription="Order Information List"
          /> */}

          <DataTablev2
            data={orderData ? orderData : []}
            totalRecords={orderData?.length}
            columns={columns}
            search={false}
            title={"Order Summary"}
            tablePagination={false}
          />

          {optionalServicesData && (
            <>
              <OptionalServicesTable optionalServicesData={optionalServicesData} />
            </>
          )}          

          <div className="couponcode-block">
            <div className="w-full flex justify-between">
              <div className="couponCode min-w-[510px] flex-1">
                  <div className="coupon-input">
                    <Input
                      type="text"
                      label="Add Discount Coupon Code (optional)"
                      required={false}
                      autoComplete="off"
                      ErrorLabel={
                        formErrors.apply_coupon ? "apply_coupon_error" : null
                      }
                      name="apply_coupon"
                      value={data.apply_coupon}
                      onInputChange={onCouponChangeHandler}
                      ref={couponRef}
                      error={formErrors?.apply_coupon ? true : false}
                      errorMsg={formErrors?.apply_coupon}
                      forLabel={"Add Discount Coupon Code (optional)"}
                    />

                    <Button
                      title={"Apply Coupon"}
                      className={"button--blue apply-btn"}
                      onClick={applyCouponHandler}
                    />
                  </div>
              </div>

              <div className="total-discount-table flex-1 min-w-[300px]">
                <table className="discountTable">
                  <caption className="visually_hidden">
                    Payable Amount
                  </caption>
                  <tbody>
                    <tr>
                      <th>
                        <strong>Total</strong>
                      </th>
                      <td>
                        <strong>{currencyFormatter(total)}</strong>
                      </td>
                    </tr>
                    <tr>
                      <th>
                        <strong>Total Discount</strong>
                      </th>
                      <td>
                        <strong>{currencyFormatter(total_discount)}</strong>
                      </td>
                    </tr>
                    <tr>
                      <th>
                        <strong>Balance Payable</strong>
                      </th>
                      <td>
                        <strong>{currencyFormatter(balance_payable)}</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>              
            </div>
          </div>
        </>
        {/* )}
        {!loading && orderData?.length <= 0 && (
          <div className="no-record-main">
            <div className="no-record">{noRecordFound}</div>
          </div>
        )} */}
      </div>
    </>
  );
};
export default OrderInformationFileTable;
